import React, { useState } from 'react';

import { ModelBase } from "@models";
import { DataType, SourceBookType } from "@dndModels/Types";

import MyTTRPGAPI from "@utilities/smart_api";

import FlexBox from '@components/layout/FlexBox';
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

interface Props {
  data_type: DataType;
  plural: string;
  objects: ModelBase[]; // Whatever the index is currently showing (after its filters).
  onDeleted: (ids: string[]) => void;
}

const onlyIn = (book_ids: string[], book_id: string) =>
  book_ids.length > 0 && book_ids.every(id => id === book_id);

// Admin tool: deletes every object the index is showing that's in the chosen source book and no other.
export default function DeleteSourceBookOnlyTool({ data_type, plural, objects, onDeleted }: Props) {
  const [open, setOpen] = useState(false);
  const [book_id, setBookId] = useState("");
  const [book_type, setBookType] = useState<SourceBookType | "">("");
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState<{ done: number, total: number, deleted: number, skipped: string[], failed: string[] } | null>(null);

  const book = api.source_books.find(o => o._id === book_id);
  const book_name = book ? (book.abbreviation || book.name) : "";
  const targets = book_id === "" ? [] : objects.filter(o => onlyIn(o.source_book_ids, book_id));
  const running = !!deleting && deleting.done < deleting.total;

  const deleteAll = async () => {
    setConfirm(false);
    const list = [...targets];
    const skipped: string[] = [];
    const failed: string[] = [];
    const deleted_ids: string[] = [];
    setDeleting({ done: 0, total: list.length, deleted: 0, skipped, failed });
    for (let i = 0; i < list.length; ++i) {
      const obj = list[i];
      try {
        // The index shows the light copies - don't delete anything whose full doc is in another book.
        const full = await api.getObjectFromAPI<ModelBase>({ data_type, id: obj._id });
        if (full && !onlyIn(full.source_book_ids, book_id)) {
          skipped.push(obj.name);
        } else {
          const res = await api.deleteObject(data_type, obj);
          if (res?.body?.message?.includes("deleted!")) {
            deleted_ids.push(obj._id);
          } else {
            failed.push(obj.name);
          }
        }
      } catch (ex) {
        console.error(ex);
        failed.push(obj.name);
      }
      setDeleting({ done: i + 1, total: list.length, deleted: deleted_ids.length, skipped: [...skipped], failed: [...failed] });
    }
    onDeleted(deleted_ids);
  };

  if (!open) {
    return (
      <FlexBox>
        <DandDButtonBox name="Delete Shown Only In Source Book" onClick={() => setOpen(true)} />
      </FlexBox>
    );
  }
  return (
    <FlexBox direction="column" className="padded" style={{ border: "1px solid rgba(128,128,128,0.5)", borderRadius: 4 }}>
      <span className="header">Delete Shown { plural } Only In a Source Book</span>
      <span>Permanently deletes the { plural.toLowerCase() } shown that are in the chosen source book and no other. Use the filters below to narrow down the list.</span>
      <SelectSourceBookBox name="Only In" id={book_id} type={book_type} disabled={running || confirm}
        onChange={(type, id) => { setBookType(type); setBookId(id); setDeleting(null); }}
      />
      { confirm ?
        <FlexBox direction="column">
          <span>
            Permanently delete { targets.length } { plural } that are only in { book_name }? This can&apos;t be undone.
            Characters and other objects that point at them aren&apos;t checked.
          </span>
          <span style={{ opacity: 0.8, maxHeight: 200, overflowY: "auto" }}>
            { targets.map(o => o.name).join(", ") }
          </span>
          <FlexBox direction="row">
            <FlexBox xs_expand={0}>
              <DandDButtonBox name={`Yes, Delete ${targets.length}`} onClick={deleteAll} />
            </FlexBox>
            <FlexBox xs_expand={0}>
              <DandDButtonBox name="Cancel" onClick={() => setConfirm(false)} />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      :
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={book_id === "" ? "Choose a Source Book" : `Delete ${targets.length} Only In ${book_name}`}
              disabled={running || targets.length === 0}
              onClick={() => setConfirm(true)}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Close" disabled={running} onClick={() => { setOpen(false); setDeleting(null); }} />
          </FlexBox>
        </FlexBox>
      }
      { deleting &&
        <span>
          { running ?
            `Deleting ${deleting.done} / ${deleting.total}...` :
            `Deleted ${deleting.deleted} of ${deleting.total}.` +
              (deleting.skipped.length > 0 ? ` Skipped (full doc is in other books): ${deleting.skipped.join(", ")}.` : "") +
              (deleting.failed.length > 0 ? ` Failed: ${deleting.failed.join(", ")}.` : "")
          }
        </span>
      }
    </FlexBox>
  );
}
